import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Paper,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  createIssueType,
  deleteIssueType,
  getIssueTypes,
  updateIssueType,
} from "../../api/services/ReferenceData/issueTypeService";
import { QK } from "../../base/qk";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import DataTable from "../../components/Table/DataTable";

const PAGE_SIZE = 10;

const IssueTypes = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const { data, isLoading } = useQuery(
    [QK.issueTypes, page],
    () => getIssueTypes(page),
    { keepPreviousData: true }
  );

  const createMutation = useMutation(createIssueType, {
    onSuccess: () => {
      enqueueSnackbar("Issue Type created successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.issueTypes);
    },
    onError: () =>
      enqueueSnackbar("Failed to create Issue Type", { variant: "error" }),
  });

  const updateMutation = useMutation(updateIssueType, {
    onSuccess: () => {
      enqueueSnackbar("Issue Type updated successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.issueTypes);
    },
    onError: () =>
      enqueueSnackbar("Failed to update Issue Type", { variant: "error" }),
  });

  const deleteMutation = useMutation(deleteIssueType, {
    onSuccess: () => {
      enqueueSnackbar("Issue Type deleted!", { variant: "success" });
      queryClient.invalidateQueries(QK.issueTypes);
      setConfirmDelete({ open: false, id: null });
    },
    onError: () =>
      enqueueSnackbar("Failed to delete Issue Type", { variant: "error" }),
  });

  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name.trim()) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }

    if (editId) {
      updateMutation.mutate({ id: editId, name });
      setEditId(null);
    } else {
      createMutation.mutate({ name });
    }

    setName("");
  };

  const handlePageChange = (_, value) => setPage(value);

  if (isLoading) return <p>Loading...</p>;

  const totalPages = Math.ceil((data?.count || 0) / PAGE_SIZE);

  const headers = [
    { key: "name", label: "Name" },
  ];

  const renderCell = (item, header) => {
    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
      <IconButton
        size="small"
        onClick={() => {
          setEditId(item.id);
          setName(item.name);
        }}
        sx={{
          color: "#667085",
          "&:hover": {
            color: "#F59E0B",
            bgcolor: "rgba(245, 158, 11, 0.06)",
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => setConfirmDelete({ open: true, id: item.id })}
        sx={{
          color: "#667085",
          "&:hover": {
            color: "#EF4444",
            bgcolor: "rgba(239, 68, 68, 0.06)",
          },
        }}
      >
        <DeleteIcon fontSize="small" color="error" />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, mb: 3, border: "1px solid #eee" }}
      >
        <form onSubmit={handleSubmit} style={{ display: "flex", gap: 16 }}>
          <TextField
            label="Issue Type Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button variant="contained" color="primary" type="submit">
            {editId ? "Update" : "Add"}
          </Button>
        </form>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3 }}>
        <DataTable
          data={data?.results || []}
          headers={headers}
          renderCell={renderCell}
          customActions={customActions}
          loading={isLoading}
        />

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>

      <ConfirmModal
        show={confirmDelete.open}
        title="Delete Issue Type"
        body="Are you sure you want to delete this issue type?"
        handleClose={() => setConfirmDelete({ open: false, id: null })}
        handleConfirm={() => deleteMutation.mutate(confirmDelete.id)}
        isLoading={deleteMutation.isLoading}
        confirmText="Delete"
        cancelText="Cancel"
        isDelete
      />
    </Box>
  );
};

export default IssueTypes;